<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
          <PageHeader showBreadcrumbs :page-title="'Partners Telegram Campaigns'">
            <template v-slot:buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                class="button"
                @click="() => showModal()"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getTableFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilters"
          />

          <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <div v-else class="full-height-content">
            <TableForPage
              v-if="getTableData.length > 0"
              ref="table"
              :isDataLoading="isDataPageLoading && isInitialDataLoaded"
              :tableData="getTableData"
              :fields="getTableFields"
              :slots="getTableSlots"
              :perPage="getPartnerTelegramCampaigns.per_page"
              :currentPage="getPartnerTelegramCampaigns.current_page"
              :totalRows="getPartnerTelegramCampaigns.total"
              :actions="getTableActions"
              isServerRender
              noLocalSorting
              @remove="remove"
              @edit="showModal"
              @start="start"
              @update="updateList"
            >
              <template v-slot:partners="data">
                <span v-if="data.item.for_all">For All</span>
                <span v-else-if="data.item.partners.length === 0">Selected telegram was removed</span>
                <div v-else v-for="telegram in data.item.partners" v-bind:key="telegram.id">
                  <span :key="telegram.id">{{ telegram.partner.company_name }}</span>
                </div>
              </template>
            </TableForPage>
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getTableData.length"
              title="No campaigns for partners"
              description="Create a campaign to send updates, resources, and project information to your partners."
              customIcon="partner_exchange"
              customIconClasses="material-symbols-outlined empty-state__icon"
              buttonText="CREATE CAMPAIGN"
              @button-click="() => showModal()"
            />
          </div>
        </div>
      </div>
    </div>

    <PartnerTelegramCampaignModal
      v-model="modal.show"
      :form="modal.form"
      :isLoading="isModalLoading"
      @save="save"
    />
  </div>
</template>

<script>
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import TableForPage from '@tables/TableForPage'
import { mapGetters } from 'vuex'
import TelegramPartnersCampaignMixin from '@mixins/notifications/TelegramPartnersCampaignMixin'
import PartnerTelegramCampaignModal from '@components/modals/Telegram/PartnerTelegramCampaignModal'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'

export default {
  mixins: [TelegramPartnersCampaignMixin],
  components: {
    PageHeader,
    PartnerTelegramCampaignModal,
    Filters,
    TableForPage,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      isAllOptionsFilterSet: false,
      isTelegramCampaignDataLoaded: false,
      isModalLoading: false
    }
  },
  created () {
    this.fetchAllData()
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isTelegramCampaignDataLoaded
    },
    ...mapGetters({
      getAvailablePartnerTelegrams: 'telegram/getAvailablePartnerTelegrams',
      getPartnerTelegramCampaigns: 'telegram/getPartnerTelegramCampaigns'
    }),
    getTableFilters () {
      return this.filters || {}
    },
    getTableFields () {
      return this.table.fields || []
    },
    getTableSlots () {
      return this.table.slots || []
    },
    getTableActions () {
      return this.table.actions || []
    },
    getTableData () {
      return this.getPartnerTelegramCampaigns.data || []
    }
  },
  methods: {
    fetchAllData () {
      return Promise.all([
        this.fetchAvailablePartners(),
        this.fetchList()
      ]).then(() => {
        this.setAllOptions()
      })
    },
    setAllOptions () {
      this.modal.form.partners.options = this.getAvailablePartnerTelegrams
      this.isAllOptionsFilterSet = true
    },
    fetchAvailablePartners () {
      return this.$api.telegram.getAvailablePartnerTelegrams()
    },
    fetchList () {
      let params = this.table.params.currentPage
      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }
      return this._toggleDataPageLoading(() => this.$api.telegram.getPartnerTelegramCampaigns(params)).then((result) => {
        this.isTelegramCampaignDataLoaded = true
        return result
      })
    },
    updateList (data) {
      if (!data) return this.fetchList()

      const { currentPage, sortDesc, sortBy } = data
      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)
      this.fetchList()
    },
    setFilters (data) {
      this.setParamsFilters(data)
      this.setParamsCurrentPage()
      this.fetchList()
    },
    save (data) {
      this.isModalLoading = true
      const campaign = this.prepareDataForRequest(data)
      const requestPromise = this.modal.campaignId
        ? this.$api.telegram.editPartnerTelegramCampaign({ id: this.modal.campaignId, ...campaign })
        : this.$api.telegram.createPartnerTelegramCampaign(campaign)
      requestPromise.then(() => {
        this.$snotify.success('Partner telegram campaign successfully saved', 'Success', this.$store.state.snotify)
        this.hideModal()
        this.fetchList()
      }).catch(err => {
        this.errorNotifications(err)
      })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    async start (item) {
      const confirm = await this.$root.$confirm.open(
        'Start',
        'Are you sure you want to start the partner telegram campaign?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const startResponse = await this.$api.telegram.startPartnerTelegramCampaign(item.id)
        this.$snotify.success(
          startResponse.data,
          'Success',
          this.$store.state.snotify
        )
      })
    },
    async remove (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the partner telegram campaign?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const deleteResponse = await this.$api.telegram.removePartnerTelegramCampaign(item.id)
        this.$snotify.success(
          deleteResponse.data,
          'Success',
          this.$store.state.snotify
        )
        this.updateList()
      })
    }
  }
}
</script>
